/**
 * Excursion cards: discount, commercial badge and key information.
 * Display only — never used for a booking amount or a commission.
 * Fields live in `details.excursion` (see backend services/excursion_highlights.py).
 */
export const DURATION_TYPES = ['half_day', 'full_day', 'custom'];
// Hours set by a half / full day (same values as the server); « custom »: typed.
export const DURATION_HOURS = { half_day: 4, full_day: 8 };
export const ACTIVITY_TYPES = ['speedboat', 'longtail', 'catamaran', 'fishing', 'wildlife', 'scuba_diving', 'other'];
// Set on the listing by an admin only (one at a time); « new » is automatic.
export const ADMIN_BADGES = ['premium', 'popular', 'best_seller'];
const BADGE_PRIORITY = ['premium', 'best_seller', 'popular'];
const NEW_DAYS = 30;
const DAY_MS = 24 * 3600 * 1000;

export const BADGE_ICONS = { new: '🆕', popular: '🔥', best_seller: '⭐', premium: '💎' };
// New green, Premium blue, Popular orange, Best Seller yellow (dark text for contrast).
export const BADGE_STYLES = {
  new: 'bg-emerald-500 text-white',
  premium: 'bg-blue-600 text-white',
  popular: 'bg-orange-500 text-white',
  best_seller: 'bg-yellow-400 text-slate-900',
};
const ACTIVITY_ICONS = { speedboat: '🚤', longtail: '🛶', catamaran: '⛵', fishing: '🎣', wildlife: '🐘', scuba_diving: '🤿' };

/** Rounded discount (%) when the reference price is really higher than the price, else 0. */
export const discountPercent = (price, referencePrice) => {
  const p = Number(price);
  const r = Number(referencePrice);
  if (!(p > 0) || !(r > p)) return 0;
  return Math.round(((r - p) / r) * 100);
};

/** One badge at most: an explicit one (premium > best seller > popular), else « New » for 30 days after creation. */
export const commercialBadge = (service, now = Date.now()) => {
  const explicit = BADGE_PRIORITY.find((b) => service?.commercial_badge === b);
  if (explicit) return explicit;
  const created = Date.parse(service?.created_at || '');
  if (Number.isFinite(created) && now >= created && now - created < NEW_DAYS * DAY_MS) return 'new';
  return null;
};

/** Key information shown under the title: only what the listing really states. */
export const excursionHighlights = (exc = {}) => {
  const out = [];
  if (exc.duration_type === 'half_day' || exc.duration_type === 'full_day') {
    out.push({ key: 'duration', icon: '🕒', labelKey: `excursionCard.duration.${exc.duration_type}` });
  } else if (Number(exc.duration_hours) > 0) {
    out.push({ key: 'duration', icon: '🕒', labelKey: 'excursionCard.duration.hours', params: { count: Number(exc.duration_hours) } });
  }
  if (ACTIVITY_ICONS[exc.activity_type]) {
    out.push({ key: 'activity', icon: ACTIVITY_ICONS[exc.activity_type], labelKey: `excursionCard.activity.${exc.activity_type}` });
  }
  if (exc.meal_included === true) out.push({ key: 'meal', icon: '🍽️', labelKey: 'excursionCard.mealIncluded' });
  return out;
};

// ─── Form helpers (create / edit dialogs) ────────────────────────────────────────────────────
export const EMPTY_HIGHLIGHTS = { reference_price: '', duration_type: '', activity_type: '', meal_included: '' };

export const highlightsFromExcursion = (exc = {}) => ({
  reference_price: exc.reference_price != null ? String(exc.reference_price) : '',
  duration_type: DURATION_TYPES.includes(exc.duration_type) ? exc.duration_type : '',
  activity_type: ACTIVITY_TYPES.includes(exc.activity_type) ? exc.activity_type : '',
  meal_included: exc.meal_included === true ? 'yes' : exc.meal_included === false ? 'no' : '',
});

/** Fields sent with the listing; an empty choice is sent as null (nothing invented). */
export const highlightsPayload = (h = EMPTY_HIGHLIGHTS) => {
  const ref = parseFloat(h.reference_price);
  return {
    reference_price: ref > 0 ? ref : null,
    duration_type: h.duration_type || null,
    activity_type: h.activity_type || null,
    meal_included: h.meal_included === 'yes' ? true : h.meal_included === 'no' ? false : null,
  };
};
